import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { isPaidPlan } from "../lib/planCapabilities";
import { getUserPlan } from "../lib/planUtils";

const CACHE_KEY = "atlas-funciones-avanzadas";

/** Evento para que el shell se entere cuando se toca el interruptor en Configuración. */
export const EVENTO_FUNCIONES_AVANZADAS = "atlas:funciones-avanzadas-cambiadas";

/**
 * Llaves de `company_profiles.funciones_avanzadas`. Cada una prende una función
 * que la empresa activa por su cuenta desde Configuración → Funciones avanzadas.
 * Es distinto de `dev_tester`, que es un flag INTERNO nuestro.
 *
 * **Todas exigen PLAN PAGO como mínimo** — ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago.
 * El corte está en `useFuncionesAvanzadas`, o sea UNA vez, y de ahí lo heredan los
 * atajos de abajo: la prueba gratis no ve la sección que las prende, y la que se haya
 * prendido con un plan pago queda inerte si la cuenta vuelve a gratis.
 *
 * `boton_flotante_chat` volvió a la regla de todas (plan pago) cuando el Agente pasó a ser
 * de todos (ADR-2026-10-12-el-agente-es-de-todos). Ver `PISO_DE_LA_FUNCION`.
 */
export interface FuncionesAvanzadas {
  // `pre_lanzamientos` ya no es una función avanzada: Tienda online e Instagram son de
  // todos y "Publicar en tu tienda" depende de tener una tienda conectada
  // (`useTiendaConectada`) — ADR-2026-10-16-tienda-online-e-instagram-para-todos. La llave
  // vieja puede seguir en la base de algunas empresas; nadie la lee.
  /** Botón "Verificar calidad" en la columna de resultados (ADR-2026-09-07-verificacion-de-calidad-en-la-columna). */
  verificacion_calidad?: boolean;
  /** Correr ese análisis SOLO, apenas termina cada imagen. Depende de `verificacion_calidad`
   *  (ADR-2026-09-07-analisis-automatico-al-generar). */
  verificacion_automatica?: boolean;
  /** El flotante "Preguntále a Atlas" de la esquina inferior derecha en desktop. Apagado
   *  por defecto (ADR-2026-09-10-el-flotante-del-chat-es-una-funcion-avanzada) y la única
   *  de las cuatro con piso de Pro/Ultra, porque abre el Agente
   *  (ADR-2026-09-14-el-agente-es-de-pro-y-ultra). */
  boton_flotante_chat?: boolean;
}

export interface EstadoFuncionesAvanzadas {
  /** Las llaves TAL CUAL están en la base, sin el corte de plan. */
  funciones: FuncionesAvanzadas;
  /** `true` mientras no se sabe: la UI no debe decidir todavía. */
  cargando: boolean;
  /**
   * El plan de la cuenta, para pasarlo a `funcionAvanzadaActiva`. `null` = todavía no se
   * sabe, y eso cuenta como GRATIS: es la convención del resto de estos gates —
   * preferible que a un pago la función le aparezca un instante después que a un gratis
   * le parpadee algo que no le corresponde
   * (ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago).
   */
  plan: string | null;
  /**
   * `true` hasta que la consulta del plan contestó, con plan o sin él. Para la pantalla que
   * muestra un cartel de "desactivado": sin esto, con `plan` todavía en `null` el cartel
   * aparecía un segundo aunque la función estuviera prendida.
   */
  planCargando: boolean;
  /** Relee de la base y avisa al resto de la app. */
  refrescar: () => Promise<void>;
}

function leerCache(): FuncionesAvanzadas {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as FuncionesAvanzadas) : {};
  } catch {
    return {};
  }
}

function guardarCache(f: FuncionesAvanzadas): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(f));
  } catch {
    /* noop */
  }
}

async function traer(): Promise<FuncionesAvanzadas | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data: profile } = await supabase
    .from("profiles")
    .select("id_company")
    .eq("id", user.id)
    .single();
  if (!profile?.id_company) return null;
  const { data: company } = await supabase
    .from("company_profiles")
    .select("funciones_avanzadas")
    .eq("id_company", profile.id_company)
    .single();
  if (!company) return null;
  return (company.funciones_avanzadas as FuncionesAvanzadas | null) ?? {};
}

/**
 * Funciones avanzadas de la empresa. Cachea en localStorage para que la entrada de
 * nav no parpadee, y escucha `atlas:funciones-avanzadas-cambiadas` para reaccionar
 * al interruptor sin recargar la página.
 */
export function useFuncionesAvanzadas(): EstadoFuncionesAvanzadas {
  const [funciones, setFunciones] = useState<FuncionesAvanzadas>(leerCache);
  const [cargando, setCargando] = useState(true);
  // El plan arranca de la semilla que deja `useCuentaAtlas` en localStorage para que el
  // gate no parpadee, y se confirma contra la base en el efecto de abajo. Misma jugada
  // que `usePlanUsuario` en el shell.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem("atlas-user-plan");
    } catch {
      return null;
    }
  });

  const [planCargando, setPlanCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const f = await traer();
      if (f) {
        setFunciones(f);
        guardarCache(f);
      }
    } catch {
      /* ante error se mantiene lo cacheado */
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      await cargar();
      if (!vivo) return;
    })();
    void getUserPlan()
      .then((p) => {
        if (vivo && p) setPlan(p);
      })
      .catch(() => {})
      .finally(() => {
        if (vivo) setPlanCargando(false);
      });
    const alCambiar = () => void cargar();
    window.addEventListener(EVENTO_FUNCIONES_AVANZADAS, alCambiar);
    return () => {
      vivo = false;
      window.removeEventListener(EVENTO_FUNCIONES_AVANZADAS, alCambiar);
    };
  }, [cargar]);

  return { funciones, cargando, plan, planCargando, refrescar: cargar };
}

/**
 * Qué plan pide CADA función. El default es plan pago —la regla de
 * ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago— y la tabla existe para la
 * única que pide más.
 *
 * Es una tabla y no un `if` adentro de `funcionAvanzadaActiva` porque el piso es un dato
 * de la función, igual que su llave y su nombre: la próxima que necesite otro piso se
 * agrega acá y no toca la regla. Y porque así la lista de excepciones se LEE — un `if`
 * suelto en el medio de la función se pierde.
 *
 * Hoy no hay ninguna: `boton_flotante_chat` pedía Pro/Ultra mientras el Agente era de esos
 * planes, y volvió al default cuando pasó a ser de todos (ADR-2026-10-12-el-agente-es-de-todos).
 */
const PISO_DE_LA_FUNCION: Partial<
  Record<keyof FuncionesAvanzadas, (plan?: string | null) => boolean>
> = {};

/**
 * ¿Está activa esta función avanzada? LA regla, en un solo lugar y sin React, para que
 * los cuatro atajos de abajo no puedan discrepar y esté cubierta por tests
 * (ADR-2026-09-12-las-funciones-avanzadas-son-de-plan-pago).
 *
 * Tres condiciones: el plan llega al piso de ESA función (pago, salvo lo que diga
 * `PISO_DE_LA_FUNCION`), la llave prendida, y —si la función es un modo de otra— la madre
 * también prendida.
 */
export function funcionAvanzadaActiva(
  funciones: FuncionesAvanzadas,
  llave: keyof FuncionesAvanzadas,
  plan: string | null | undefined,
): boolean {
  const llega = PISO_DE_LA_FUNCION[llave] ?? isPaidPlan;
  if (!llega(plan)) return false;
  if (funciones[llave] !== true) return false;
  // El análisis automático no es una función aparte sino un MODO de la verificación: con
  // la verificación apagada no habría dónde ver el resultado
  // (ADR-2026-09-07-analisis-automatico-al-generar).
  if (llave === "verificacion_automatica" && funciones.verificacion_calidad !== true)
    return false;
  return true;
}

/** Escribe una función avanzada y avisa al resto de la app. */
export async function guardarFuncionAvanzada(
  idCompany: string,
  llave: keyof FuncionesAvanzadas,
  valor: boolean,
): Promise<boolean> {
  const { data: actual } = await supabase
    .from("company_profiles")
    .select("funciones_avanzadas")
    .eq("id_company", idCompany)
    .single();
  const previas = ((actual?.funciones_avanzadas as FuncionesAvanzadas | null) ?? {}) as FuncionesAvanzadas;
  const siguientes: FuncionesAvanzadas = { ...previas, [llave]: valor };
  const { error } = await supabase
    .from("company_profiles")
    .update({ funciones_avanzadas: siguientes })
    .eq("id_company", idCompany);
  if (error) {
    console.error("[funcionesAvanzadas] guardar", error);
    return false;
  }
  guardarCache(siguientes);
  window.dispatchEvent(new Event(EVENTO_FUNCIONES_AVANZADAS));
  return true;
}

/** Atajo: ¿esta empresa tiene el botón de verificación de calidad? Exige plan pago. */
export function useVerificacionCalidad(): boolean {
  const { funciones, plan } = useFuncionesAvanzadas();
  return funcionAvanzadaActiva(funciones, "verificacion_calidad", plan);
}

/**
 * ¿Se analiza sola cada imagen recién generada? Exige las DOS llaves: el análisis
 * automático no es una función aparte sino un modo de la verificación, y con la
 * verificación apagada no habría dónde ver el resultado
 * (ADR-2026-09-07-analisis-automatico-al-generar).
 */
export function useAnalisisAutomatico(): boolean {
  const { funciones, plan } = useFuncionesAvanzadas();
  return funcionAvanzadaActiva(funciones, "verificacion_automatica", plan);
}

/**
 * Atajo: ¿esta empresa tiene el flotante "Preguntále a Atlas"? Apagado por defecto y
 * exige **Pro/Ultra** — es la puerta del Agente al costado, y desde
 * ADR-2026-09-14-el-agente-es-de-pro-y-ultra ése es el piso del Agente entero. Antes le
 * alcanzaba cualquier plan pago (ADR-2026-09-12-prompt-libre-y-el-chat-salen-de-interno);
 * la llave que un Básico dejó prendida queda inerte y vuelve sola si sube de plan.
 */
export function useBotonFlotanteChat(): boolean {
  const { funciones, plan } = useFuncionesAvanzadas();
  return funcionAvanzadaActiva(funciones, "boton_flotante_chat", plan);
}
